import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ProjectContentItemDc } from "@evergis/api";

import { getProjectLayers } from "../../../ducks/project/selectors";
import { useProjectLayers } from "../../../hooks/project/useProjectLayers";
import { isObject } from "../../../utils/isObject";
import { Layers, setLayersMeta } from "../../../ducks/project/project";
import { LayerInfo } from "../../../interfaces/resourceInfo";

export const useDashboardProjectLayers = () => {
  const dispatch = useDispatch();
  const layerInfos = useSelector(getProjectLayers);
  const projectLayers = useProjectLayers();

  const processedProjectLayers = useMemo(() => {
    const processLayer = (layer?: ProjectContentItemDc | null): ProjectContentItemDc[] => {
      if (!layer) {
        return [];
      }

      if (layer.children) {
        return (
          layer.children?.reduce(
            (acc: ProjectContentItemDc[], curr) => [...acc, ...processLayer(curr as ProjectContentItemDc)],
            [],
          ) ?? []
        );
      }

      return [layer] as ProjectContentItemDc[];
    };

    return projectLayers.reduce((acc: ProjectContentItemDc[], curr) => {
      return [...acc, ...processLayer(curr)];
    }, []);
  }, [projectLayers]);

  useEffect(() => {
    if (
      Array.isArray(projectLayers) &&
      projectLayers.length > 0 &&
      isObject(layerInfos) &&
      Object.keys(layerInfos).length === 0
    ) {
      dispatch(
        setLayersMeta(
          processedProjectLayers.reduce((acc: Layers, curr) => {
            const layerInfo = layerInfos[curr.name] as LayerInfo;

            return {
              ...acc,
              [curr.name]: {
                condition: layerInfo?.condition,
                lastCount: null,
                count: null,
                alias: layerInfo?.alias,
                isVisible: curr.isVisible,
                geometryAttribute: layerInfo?.layerDefinition?.geometryAttribute,
                children: curr.children,
                type: layerInfo?.type,
              },
            };
          }, {}),
        ),
      );
    }
  }, [layerInfos, projectLayers]);
};
